import { OpportunityParticipant, type User } from "@entities";
import { MenuItem, Select, type SelectChangeEvent } from "@mui/material";
import { linko } from "@shared/linko";
import {
  PARTICIPANT_STATUS,
  PARTICIPANT_STATUS_HEB,
} from "@shared/helpers/constants";
import { type ColumnProps, type HyperTableDef } from "@shared/frontend/hyper-table/types";
import { useQueryClient } from "@tanstack/react-query";
import { useAlert } from "../../../contexts/AlertProvider";
import useQuestion from "../../../dialog-components/QuestionDialog";
import { invalidateEventParticipantQueries } from "../../../queries/invalidate-opportunities-table";
import UserNameAndPhoto from "../users-table/volunteers/components/UserNameAndPhoto";
import PhoneNumberCell from "../PhoneNumberCell";

const STATUS_SELECT_SX = {
  APPROVED: {
    backgroundColor: "success.light",
    color: "success.dark",
  },
  PENDING: {
    backgroundColor: "warning.light",
    color: "warning.dark",
  },
  DECLINED: {
    backgroundColor: "grey.300",
    color: "text.primary",
  },
} as const;

export const useOpportunityParticipantColumns = (
  hyperTable: HyperTableDef<OpportunityParticipant>,
): ColumnProps<OpportunityParticipant>[] => {
  const { setAlertText, setSeverity } = useAlert();
  const question = useQuestion();
  const queryClient = useQueryClient();

  const updateStatus = async (
    participant: OpportunityParticipant,
    nextStatus: (typeof PARTICIPANT_STATUS)[number],
  ) => {
    if (participant.status === nextStatus) return;
    if (nextStatus === "PENDING") {
      setAlertText("לא ניתן להחזיר לסטטוס ממתין");
      setSeverity("warning");
      return;
    }

    const volunteerName = participant.association?.user?.name || "המתנדב";
    const nextLabel = PARTICIPANT_STATUS_HEB[nextStatus];
    const confirmed = await question(
      `האם אתה בטוח שברצונך לשנות את הסטטוס של ${volunteerName} ל"${nextLabel}"?`,
      nextStatus === "APPROVED"
        ? "המתנדב יאושר ויקבל התראת שיבוץ."
        : "המתנדב יידחה ויוסר מהמשימה הפעילה.",
    );
    if (!confirmed) return;

    try {
      if (participant.status === "PENDING") {
        await linko.opportunityParticipant.mutations.approveOrDeclineInterested(
          {
            opportunityId: participant.opportunityId,
            associationId: participant.associationId,
            isApproved: nextStatus === "APPROVED",
          },
        );
      } else if (
        participant.status === "APPROVED" &&
        nextStatus === "DECLINED"
      ) {
        await OpportunityParticipant.approveOrDeclineVolunteer({
          participantId: participant.id,
          isApproved: false,
        });
      } else if (
        participant.status === "DECLINED" &&
        nextStatus === "APPROVED"
      ) {
        await linko.opportunityParticipant.mutations.addVolunteerToOpportunityAndNotify(
          {
            opportunityId: participant.opportunityId,
            associationId: participant.associationId,
          },
        );
      } else {
        setAlertText("לא ניתן לשנות לסטטוס זה מכאן");
        setSeverity("warning");
        return;
      }
      await Promise.all([
        hyperTable.context.current?.refetch("invalidate"),
        invalidateEventParticipantQueries(
          queryClient,
          participant.opportunityId,
        ),
      ]);
      setAlertText("הסטטוס עודכן בהצלחה");
      setSeverity("success");
    } catch {
      setAlertText("לא ניתן לעדכן את הסטטוס");
      setSeverity("error");
    }
  };

  return [
    {
      field: "association.userName",
      caption: "שם מלא",
      enableSorting: true,
      cell: ({ row }) => {
        return (
          <UserNameAndPhoto user={row.original.association?.user as User} />
        );
      },
    },
    {
      field: "association.user.phoneNumber",
      caption: "טלפון",
      cell: ({ row }) => (
        <PhoneNumberCell
          phoneNumber={row.original.association?.user?.phoneNumber || ""}
        />
      ),
      preventRowClick: true,
    },
    {
      field: "status",
      caption: "סטטוס",
      preventRowClick: true,
      minSize: 170,
      size: 180,
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Select
            size="small"
            value={status}
            onClick={(e) => e.stopPropagation()}
            onChange={(e: SelectChangeEvent) => {
              void updateStatus(
                row.original,
                e.target.value as (typeof PARTICIPANT_STATUS)[number],
              );
            }}
            sx={{
              width: "100%",
              ...STATUS_SELECT_SX[status],
              /* eslint-disable @typescript-eslint/naming-convention -- MUI sx */
              "& .MuiSelect-select": {
                py: 0.75,
                fontWeight: 600,
              },
              /* eslint-enable @typescript-eslint/naming-convention -- MUI sx */
            }}
          >
            {PARTICIPANT_STATUS.filter(
              (value) => value === status || value !== "PENDING",
            ).map((value) => (
              <MenuItem key={value} value={value}>
                {PARTICIPANT_STATUS_HEB[value]}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: "association.user.numberID",
      caption: "מספר זהות",
    },
    {
      field: "association.user.email",
      caption: "דואר אלקטרוני",
    },
    {
      field: "association.user.dateOfBirth",
      caption: "תאריך לידה",
      valueConverter: (row) => {
        const dob = row.association?.user?.dateOfBirth;
        if (!dob) return "-";
        return new Date(dob).toLocaleDateString("he");
      },
    },
    {
      field: "association.user.skills.skill.name",
      caption: "כישורים",
      valueConverter: (row) => {
        return (
          row.association?.user?.skills
            ?.map((skill) => skill.skill?.name)
            ?.join(" ,") || "-"
        );
      },
    },
    {
      field: "association.user.gender",
      caption: "מגדר",
      valueConverter: (row) => {
        const gender = row.association?.user?.gender;
        if (gender === "MALE") return "זכר";
        if (gender === "FEMALE") return "נקבה";
        if (gender === "OTHER") return "אחר";
        return "-";
      },
    },
    {
      field: "createdAt",
      caption: "תאריך הרשמה",
      valueConverter: (row) => {
        return new Date(row.createdAt || "").toLocaleString("he");
      },
    },
  ];
};

export default useOpportunityParticipantColumns;
